/**
 * Re-exports core types from `@graftus/schema` and provides a client-friendly
 * `ProductEntitlement` (state plus the validity window — the full schema type
 * includes server fields like `source` and `updatedAt` that Firestore
 * listeners don't carry).
 *
 * The access logic mirrors `packages/schema/src/product-entitlement.ts`; the
 * timestamp parsing and the account-level predicate are imported from there
 * rather than re-implemented, so the two copies cannot drift on the parts that
 * are easy to get subtly wrong.
 */
export type { ProductKey, EntitlementState } from "@graftus/schema";

import type {
  ProductKey,
  EntitlementState,
  TimestampLike,
} from "@graftus/schema";
import { isAccountExpired, toEpochMillis } from "@graftus/schema";

export { isAccountExpired };

export interface ProductEntitlement {
  state: EntitlementState;
  /** Start of the entitlement window (manual grants). */
  validFrom?: TimestampLike | null;
  /** End of the entitlement window (manual grants, trials). */
  validUntil?: TimestampLike | null;
}

export type ProductEntitlements = Partial<Record<ProductKey, ProductEntitlement>>;

/** The account-level and per-product fields the access helpers read. */
export interface ProductAccessBusiness {
  productEntitlements?: ProductEntitlements;
  interestedProducts?: string[];
  leadsEnabled?: boolean;
  subscription?: { status: string } | null;
  accountType?: string;
  /** Hard expiry for the whole account; unset means open-ended. */
  accessValidUntil?: TimestampLike | null;
}

const ACTIVE_STATES: ReadonlySet<EntitlementState> = new Set([
  "active",
  "trialing",
  "grace",
]);

/**
 * True when `now` falls inside the entitlement's own `validFrom`/`validUntil`
 * window. Absent bounds are treated as open-ended on that side.
 */
function isEntitlementWindowOpen(
  entitlement: ProductEntitlement,
  now: number
): boolean {
  const from = toEpochMillis(entitlement.validFrom);
  if (from != null && now < from) return false;
  const until = toEpochMillis(entitlement.validUntil);
  if (until != null && now > until) return false;
  return true;
}

/**
 * Check whether a specific product is accessible from entitlements alone
 * (no legacy fallback), honouring both the account-level ceiling and the
 * entitlement's own validity window.
 */
export function hasProductAccess(
  business: ProductAccessBusiness | null | undefined,
  key: ProductKey,
  now: number
): boolean {
  if (isAccountExpired(business, now)) return false;
  const ent = business?.productEntitlements?.[key];
  if (ent == null) return false;
  return ACTIVE_STATES.has(ent.state) && isEntitlementWindowOpen(ent, now);
}

/**
 * Same check but with fallback to legacy fields for businesses that have not
 * been backfilled yet.
 *
 * The account-level ceiling wins over every fallback: an expired account has
 * no product access even if the legacy signals say otherwise.
 */
export function hasProductAccessWithFallback(
  business: ProductAccessBusiness,
  key: ProductKey,
  now: number
): boolean {
  if (isAccountExpired(business, now)) return false;

  const ent = business.productEntitlements?.[key];
  if (ent != null) {
    return ACTIVE_STATES.has(ent.state) && isEntitlementWindowOpen(ent, now);
  }

  switch (key) {
    case "recruit":
      if (
        business.subscription?.status === "active" ||
        business.subscription?.status === "trialing"
      ) {
        return true;
      }
      return ["pro", "premium", "unlimited"].includes(
        business.accountType ?? ""
      );

    case "leads":
      return business.leadsEnabled === true;

    case "intelligence":
      return false;

    default:
      return false;
  }
}
